import type {CSSProperties} from "react";
import styled, {css, keyframes} from "styled-components";
import {fontFamilies, motion, shadows} from "../../theme/tokens";
import {COMPOSER_SLIDE_MS, type RequestPlacement, type SendPlacement} from "home/request/composerSlide";
import type {previewPersonalizationAccent} from "home/personalization/personalizationAccent";
import {UnderlineField} from "./FormField";
import {TileGlyph} from "./TemplateTilePicker.styles";

/**
 * The panel body: a flex column inside `CreateFlow`'s capped-height `Card`. `min-height: 0` lets it
 * shrink below its content so the shrink reaches the file list (ui#216).
 */
export const Root = styled.div.attrs<{$accent: ReturnType<typeof previewPersonalizationAccent>}>(({$accent, theme}) => ({
	style: {
		"--sender-accent": $accent.accent,
		"--sender-ink-on-accent": $accent.inkOnAccent,
		// Leave the tint absent too: the unpersonalized tabs and payload use different product washes.
		"--sender-accent-tint": $accent.accent === undefined ? undefined
			: `color-mix(in srgb, var(--sender-accent) 14%, ${theme.tokens.card})`,
	} as CSSProperties,
}))`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
`;

/**
 * The sender controls as one inertable subtree. CardSheets and their nested modal dialogs stay siblings
 * outside it, so obscuring the panel never disables the active editor or the global navigation.
 */
export const PanelControls = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
`;

/**
 * The box the send and request composers share (kitecorp/transferify-ui#3140). In the panel's flex chain
 * like the composers it holds, so the payload box stays the card's one shrinkable child. While the request
 * composer slides it clips it, and `useComposerSlide` carries its height across.
 */
export const ComposerStage = styled.div<{$sliding: boolean}>`
	position: relative;
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	overflow: ${({$sliding}) => ($sliding ? "hidden" : "visible")};
`;

/** The send composer: in the flow, or held still beneath the request composer as it slides in over it. */
export const SendLayer = styled.div<{$placement: SendPlacement}>`
	display: ${({$placement}) => ($placement === "hidden" ? "none" : "flex")};
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	${({$placement}) => $placement === "beneath" && css`
		position: absolute;
		inset: 0 0 auto 0;
	`}
`;

const slideIn = keyframes`
	from { transform: translateX(100%); }
	to { transform: translateX(0); }
`;

const slideOut = keyframes`
	from { transform: translateX(0); }
	to { transform: translateX(100%); }
`;

/**
 * The request composer: on top of the send one, on the card's own surface so nothing beneath shows
 * through, sliding in from the right and back out to it (owner 2026-10-01). Sliding out it is already off
 * the panel's flow, so the panel's height is the send composer's.
 */
export const RequestLayer = styled.div<{$placement: RequestPlacement}>`
	display: ${({$placement}) => ($placement === "hidden" ? "none" : "flex")};
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	position: relative;
	z-index: 1;
	background: ${({theme}) => theme.tokens.card};
	${({$placement}) => $placement === "slidingIn" && css`
		animation: ${slideIn} ${COMPOSER_SLIDE_MS}ms ${motion.easeOut} both;
	`}
	${({$placement}) => $placement === "slidingOut" && css`
		position: absolute;
		inset: 0 0 auto 0;
		animation: ${slideOut} ${COMPOSER_SLIDE_MS}ms ${motion.easeIn} both;
	`}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

/**
 * The notice slot above the payload: the unverified-address callout (ui#1458) and the download-usage
 * banner (ui#309). Neither notice styles its own outside — the callout also renders in the account
 * sheet, where this panel's rhythm does not apply — so the separation belongs to this wrapper.
 *
 * 14px is the panel's block rhythm, the same value {@link ModeRow} and {@link FullWidthPanelButton}
 * already use, rather than a spacing scale: this file has none.
 *
 * The gap is NOT on {@link PanelControls} itself. Its other children space themselves through a
 * `margin-top` on their own first child (6px, 12px or 14px by block), and {@link SubmitButton} carries
 * that margin to six other surfaces, so a gap there would compound into every one of them instead of
 * replacing anything.
 */
export const Notices = styled.div`
	display: flex;
	flex-direction: column;
	gap: 14px;
	margin-bottom: 14px;

	/*
	 * Both notices render null far more often than not - a confirmed address and usage under 80% is
	 * the ordinary case - and an empty flex box would still spend its margin, pushing the payload down
	 * for users who were shown nothing.
	 */
	&:empty {
		display: none;
	}
`;

/**
 * The fields BELOW the payload box — "Your email", the mode row, the Options button and the over-cap
 * nudge (ui#216). `flex: none`: this block is pinned and never scrolls.
 *
 * It replaces the old `Scroll` region, which owned `overflow-y: auto` and dragged the whole body —
 * fields and all — under one scrollbar. The card now has exactly ONE scroller, `ListScroll` in
 * {@link ./FileSummary.styles}, and every other child of {@link Root} is `flex: none`; that is what
 * keeps the payload header pinned and confines scrolling to the file rows. Do not reintroduce an
 * `overflow` here — a second scroll region nests inside the first and the pinning breaks.
 */
export const Body = styled.div`
	flex: none;
`;

/**
 * "Your email" under the box above it, the picked files or a request's note, the same gap in both modes. It
 * was 2px, which put the field against the box and drew it over the bottom of the note box's focus ring, a
 * 2px outline 2px out (owner 2026-10-01). 6px clears that ring with a gap left to see, and no more, because
 * this gap is spent from the stacked phone card's height budget (`campaignSpace.STACKED_CARD_HEIGHT_BUDGET_PX`),
 * which the panel's 14px rhythm here would overrun.
 */
export const Field = styled(UnderlineField)`
	margin-top: 6px;
`;

export const Foot = styled.div`
	flex: none;
`;

/** Row holding the relocated [Instant | Upload] mode selector (ui#214). */
export const ModeRow = styled.div`
	margin-top: 14px;
`;

/** Shared full-width secondary panel action, retained for CreateFlow's delivery-retry controls. */
export const FullWidthPanelButton = styled.button`
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	height: 48px;
	margin-top: 14px;
	padding: 0 14px;
	border: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.card};
	border-radius: 12px;
	cursor: pointer;
	font: 600 14px ${fontFamilies.sans};
	color: ${({theme}) => theme.tokens.ink};
	transition: 0.16s;
	box-shadow: 0 1px 2px rgba(16, 23, 42, 0.05);

	&:hover {
		border-color: ${({theme}) => theme.tokens.selectedEdge};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 1px;
	}
`;

/**
 * The `[Options | Personalise]` pair (ui#556): two sheet openers sharing one rail.
 *
 * A ROW OF BUTTONS, not a `SegmentedTabs`. The two controls open dialogs rather than switching a view,
 * so tab semantics would tell a screen-reader user that one of them is the "selected" panel when
 * neither is — `aria-haspopup="dialog"` is what these actually are. At 375px the pair stacks, because
 * two 48px controls side by side leave each of them too narrow to read its own label.
 */
export const ControlPair = styled.div`
	display: flex;
	gap: 8px;
	margin-top: 14px;

	@media (max-width: 420px) {
		flex-direction: column;
	}
`;

/**
 * One opener inside {@link ControlPair}. Same affordance as the standalone Options button it replaced
 * — same height, border, radius and focus ring — so the pair reads as the control the panel already
 * had, split in two.
 */
export const PairButton = styled.button`
	display: flex;
	align-items: center;
	gap: 10px;
	flex: 1 1 0;
	min-width: 0;
	height: 48px;
	padding: 0 14px;
	border: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.card};
	border-radius: 12px;
	cursor: pointer;
	font: 600 14px ${fontFamilies.sans};
	color: ${({theme}) => theme.tokens.ink};
	transition: 0.16s;
	box-shadow: 0 1px 2px rgba(16, 23, 42, 0.05);

	&:hover {
		border-color: ${({theme}) => theme.tokens.selectedEdge};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 1px;
	}

	/*
	 * The flex shorthand above is about WIDTH — equal halves of one rail. Once ControlPair stacks,
	 * the very same declaration governs HEIGHT instead, and a zero basis that may shrink beats the
	 * 48px stated above: measured, these collapsed to 20px on a 375px phone, well under a thumb's
	 * target. Resetting it here lets the stated height win on the axis it was written for.
	 */
	@media (max-width: 420px) {
		flex: none;
	}
`;

/**
 * The "on" marker on the Personalise opener: a lone ✓ chip once this transfer overrides the account
 * look. A glyph, not a sentence (ui#813) — "Schimbat pentru acest transfer" overflowed the pill, the
 * same disease the price tag died of (ui#784); the full wording stays as the chip's accessible text.
 * It sits on the button because that is the only place the create panel can say a transfer is
 * personalized without opening the sheet to find out.
 */
export const PairButtonTag = styled.span`
	margin-left: auto;
	flex: none;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 18px;
	border-radius: 50%;
	background: ${({theme}) => theme.tokens.actionFill};
	color: ${({theme}) => theme.tokens.actionInk};
	font-size: 11px;
	font-weight: 700;
	line-height: 1;
`;

/**
 * The scene a Halloween slide put on this transfer (kitecorp/transferify-ui#3776): one row under the opener pair,
 * its own tile, its name and the off switch. The same border, radius and ground as a {@link PairButton}, so it
 * reads as the pair's third line rather than as a notice.
 */
export const SceneThemeRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-top: 8px;
	padding: 4px 12px 4px 4px;
	border: 1px solid ${({theme}) => theme.tokens.line};
	border-radius: 12px;
	background: ${({theme}) => theme.tokens.card};
`;

/** The scene's own tile: the picker's glyph at its own height, a thumbnail's width. */
export const SceneThemeThumb = styled(TileGlyph)`
	width: 64px;
	flex: none;
`;

export const SceneThemeLabel = styled.span`
	flex: 1 1 auto;
	min-width: 0;
	font: 600 14px ${fontFamilies.sans};
	color: ${({theme}) => theme.tokens.ink};
`;

/** Leading tinted glyph tile inside the Options button. */
export const OptionsButtonIcon = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The panel's ONE alert region (ui#763): why the last press of Send did not send.
 *
 * It belongs on the PANEL, not only in the composer, because that is where the sender is standing when
 * they press the button — the composer's own error line is unmounted along with its sheet, so a draft
 * that is armed but not yet sendable used to turn Send into a dead click with nothing at all on screen
 * to explain it. Placed directly under the `[Options | Personalise]` pair so the reason sits beside the
 * control that fixes it.
 */
export const SendBlocked = styled.p`
	margin: 10px 0 0;
	font-size: 12.5px;
	font-weight: 600;
	line-height: 1.4;
	color: ${({theme}) => theme.tokens.errorInk};
`;

export const SubmitButton = styled.button`
	width: 100%;
	height: 46px;
	border: none;
	border-radius: 14px;
	/* The filled-button role (ui#2935), and its ink: an account colour picks black or white, and blue's
	   own ink in dark mode is dark, since white on the dark-mode blue measures 3.28:1. */
	background: ${({theme}) => theme.tokens.actionFill};
	color: ${({theme}) => theme.tokens.actionInk};
	font: 700 14.5px ${fontFamilies.sans};
	cursor: pointer;
	margin-top: 12px;
	transition: background 180ms ease, box-shadow 180ms ease, transform 180ms ease;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 9px;

	&:hover:not(:disabled) {
		background: ${({theme}) => theme.tokens.actionFillHover};
		transform: translateY(-1px);
		box-shadow: ${shadows.popover};
	}

	&:active:not(:disabled) {
		transform: translateY(0);
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	&:disabled {
		background: ${({theme}) => theme.tokens.field};
		color: ${({theme}) => theme.tokens.ink3};
		cursor: not-allowed;
	}
`;

export const RecipientStatus = styled.div`
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 12.5px;
	line-height: 1.4;
	margin-top: 8px;

	&:empty {
		display: none;
	}
`;

export const PickerActions = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	min-width: 0;
`;

export const RecipientChipWrap = styled.span`
	display: flex;
	min-width: 0;
	max-width: 100%;

	> span {
		min-width: 0;
		max-width: 100%;
		overflow-wrap: anywhere;
	}

	/* Compact 24px circular remove control, vertically centered inside the ~28px pill (ui#213). The
	 * owner accepts trading the strict 44px WCAG touch target for the compact look; kept at 24px so it
	 * stays comfortably tappable and perfectly round. */
	button {
		width: 24px;
		height: 24px;
		min-width: 24px;
		min-height: 24px;
		flex: none;
		align-self: center;
		border-radius: 50%;
	}
`;
